import {
  Schema,
  SchemaPropertyWithOptionalType,
} from "common/flowmaster/types/schema";

const validateRequiredArray = (
  properties: Record<string, unknown>,
  required: string[],
  path = "",
): string[] => {
  const errors: string[] = [];
  const propertyNames = Object.keys(properties || {});
  const prefix = path ? `${path}` : "";

  propertyNames.forEach(propName => {
    if (!required.includes(propName)) {
      errors.push(
        `${prefix}Property '${propName}' is not included in the required array`,
      );
    }
  });

  required.forEach(reqProp => {
    if (!propertyNames.includes(reqProp)) {
      errors.push(
        `${prefix}Required array contains non-existent property '${reqProp}'`,
      );
    }
  });

  return errors;
};

const validateSchemaProperty = (
  property: SchemaPropertyWithOptionalType,
  path = "",
): string[] => {
  const errors: string[] = [];

  if (!property || typeof property !== "object") {
    return errors;
  }

  const prefix = path ? `${path}.` : "";
  const allowedPropertyKeys = new Set([
    "type",
    "description",
    "enum",
    "properties",
    "items",
    "required",
  ]);

  Object.keys(property).forEach(key => {
    if (!allowedPropertyKeys.has(key)) {
      errors.push(`${prefix}${key}: '${key}' is not a valid schema property`);
    }
  });

  if (property.type) {
    const validTypes = ["string", "number", "object", "array"];

    if (Array.isArray(property.type)) {
      if (
        property.type.length !== 2 ||
        !validTypes.includes(property.type[0]) ||
        property.type[1] !== "null"
      ) {
        errors.push(
          `${prefix}type: Invalid optional type format. Must be [validType, "null"]`,
        );
      }
    } else if (!validTypes.includes(property.type)) {
      errors.push(
        `${prefix}type: '${
          property.type
        }' is not a valid type. Must be one of: ${validTypes.join(", ")}`,
      );
    }
  }

  if (property.enum) {
    if (!Array.isArray(property.enum)) {
      errors.push(`${prefix}enum: Must be an array`);
    } else {
      const coreType = Array.isArray(property.type)
        ? property.type[0]
        : property.type;
      if (coreType && !["string", "number"].includes(coreType)) {
        errors.push(`${prefix}enum: Only allowed for string and number types`);
      }
    }
  }

  if (property.properties) {
    const coreType = Array.isArray(property.type)
      ? property.type[0]
      : property.type;
    if (coreType !== "object") {
      errors.push(`${prefix}properties: Only allowed for object type`);
    } else {
      Object.entries(property.properties).forEach(([propName, propValue]) => {
        const nestedPath = `${prefix}properties.${propName}`;
        const nestedErrors = validateSchemaProperty(propValue, nestedPath);
        errors.push(...nestedErrors);
      });
    }
  }

  if (property.items) {
    const coreType = Array.isArray(property.type)
      ? property.type[0]
      : property.type;
    if (coreType !== "array") {
      errors.push(`${prefix}items: Only allowed for array type`);
    } else {
      const itemPath = `${prefix}items`;
      const itemErrors = validateSchemaProperty(property.items, itemPath);
      errors.push(...itemErrors);
    }
  }

  return errors;
};

export const validateSchema = (
  schema: Schema | Record<string, unknown>,
  isParametersOnly = false,
): string[] => {
  const errors: string[] = [];

  if (!schema || typeof schema !== "object") {
    errors.push("Schema must be an object");
    return errors;
  }

  if (isParametersOnly) {
    const allowedParameterKeys = new Set(["type", "properties", "required"]);

    Object.keys(schema).forEach(key => {
      if (!allowedParameterKeys.has(key)) {
        errors.push(
          `'${key}' is not a valid parameter property. Allowed: type, properties, required`,
        );
      }
    });

    if ("properties" in schema && schema.properties) {
      Object.entries(
        schema.properties as Record<string, SchemaPropertyWithOptionalType>,
      ).forEach(([propName, property]) => {
        const propPath = `properties.${propName}`;
        const propErrors = validateSchemaProperty(property, propPath);
        errors.push(...propErrors);
      });

      if (
        "required" in schema &&
        schema.required &&
        Array.isArray(schema.required)
      ) {
        const requiredErrors = validateRequiredArray(
          schema.properties as Record<string, SchemaPropertyWithOptionalType>,
          schema.required,
        );
        errors.push(...requiredErrors);
      } else if (
        schema.properties &&
        Object.keys(schema.properties).length > 0
      ) {
        errors.push("Required array is missing or not an array");
      }
    }
  } else {
    const allowedSchemaKeys = new Set(["name", "description", "parameters"]);

    Object.keys(schema).forEach(key => {
      if (!allowedSchemaKeys.has(key)) {
        errors.push(
          `'${key}' is not a valid schema property. Allowed: name, description, parameters`,
        );
      }
    });

    if ("parameters" in schema && schema.parameters) {
      const paramErrors = validateSchema(
        schema.parameters as Schema["parameters"],
        true,
      );
      errors.push(...paramErrors.map(err => `parameters.${err}`));
    }
  }

  return errors;
};
